import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import type { Task, Lead } from '@/types';
import { getLegacyCustomerName } from '@/types';
import { db } from '@/lib/firebase';
import { doc, updateDoc, collection, addDoc, Timestamp } from 'firebase/firestore';

/**
 * Unified Notification Service
 * Handles both Local Notifications (reminders) and FCM Push Notifications
 */

// iOS hard-caps pending local notifications at 64. Stay under it so there is room
// for reminders scheduled after startup (new tasks, snoozes) without the OS quietly
// evicting the ones we just set.
const IOS_PENDING_NOTIFICATION_LIMIT = 60;

/** Reminder time in epoch ms, for ordering. Callers must ensure dueDate is set. */
function getReminderTime(task: Task): number {
  const dueDate = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate!);
  return dueDate.getTime();
}

// Track initialization state
let isInitialized = false;
let notificationTapHandler: ((notification: { type: string; data: Record<string, unknown> }) => void) | undefined;
let taskCompletionHandler: ((taskId: string) => Promise<void>) | undefined;
let remindLaterHandler: ((taskId: string) => Promise<void>) | undefined;

/**
 * Initialize the notification service
 * Creates channels, requests permissions, sets up listeners
 * Should be called once on app startup
 */
export async function initializeNotifications(
  onNotificationTap?: (notification: { type: string; data: Record<string, unknown> }) => void,
  onTaskComplete?: (taskId: string) => Promise<void>,
  onRemindLater?: (taskId: string) => Promise<void>
): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    console.log('Not on native platform, skipping notification initialization');
    return;
  }

  if (isInitialized) {
    console.log('Notification service already initialized');
    return;
  }

  try {
    // Store tap handler for later use
    notificationTapHandler = onNotificationTap;
    taskCompletionHandler = onTaskComplete;
    remindLaterHandler = onRemindLater;

    // Request all permissions upfront
    await requestAllPermissions();

    // Create all notification channels
    await createAllChannels();

    // Register action types for notification buttons (non-blocking)
    registerActionTypes().catch(err => {
      console.error('Failed to register action types (notifications will still work):', err);
    });

    // Setup FCM listeners
    await setupFCMListeners();

    // Setup local notification tap handlers
    await setupLocalNotificationTapHandlers();

    isInitialized = true;
    console.log('Notification service initialized successfully');
  } catch (error) {
    console.error('Error initializing notification service:', error);
  }
}

/**
 * Request all notification permissions (Local + FCM)
 */
async function requestAllPermissions(): Promise<void> {
  try {
    // Request local notification permissions
    const localPermission = await LocalNotifications.requestPermissions();
    console.log('Local notification permission:', localPermission.display);

    // Request FCM permissions
    const fcmPermission = await FirebaseMessaging.requestPermissions();
    console.log('FCM permission:', fcmPermission.receive);
  } catch (error) {
    console.error('Error requesting permissions:', error);
  }
}

/**
 * Create all notification channels upfront
 */
async function createAllChannels(): Promise<void> {
  try {
    // Channel for task reminders (local notifications)
    await LocalNotifications.createChannel({
      id: 'dsa-reminders',
      name: 'DSA Lens Reminders',
      description: 'Important reminders for your DSA tasks',
      importance: 5, // Max importance for heads-up notifications
      visibility: 1, // Show on lock screen
      vibration: true,
      lights: true,
      lightColor: '#FF0000', // Red for important reminders
    });

    // Channel for FCM push notifications (announcements, updates, etc.)
    await LocalNotifications.createChannel({
      id: 'dsa-push',
      name: 'Updates & Announcements',
      description: 'Push notifications from server',
      importance: 5,
      visibility: 1,
      vibration: true,
      lights: true,
      lightColor: '#FF0000',
    });

    console.log('All notification channels created');
  } catch (error) {
    console.error('Error creating notification channels:', error);
  }
}

/**
 * Register notification action types
 */
async function registerActionTypes(): Promise<void> {
  try {
    await LocalNotifications.registerActionTypes({
      types: [
        {
          id: 'TASK_ACTIONS',
          actions: [
            {
              id: 'mark-done',
              title: '✓ Mark Done',
              destructive: false,
              requiresAuthentication: false,
              foreground: false, // Don't open the app
            },
            {
              id: 'remind-later',
              title: '⏰ Remind Later',
              destructive: false,
              requiresAuthentication: false,
              foreground: false, // Don't open the app
            },
          ],
        },
      ],
    });
    console.log('Notification action types registered');
  } catch (error) {
    console.error('Error registering action types:', error);
  }
}

/**
 * Setup FCM notification listeners
 */
async function setupFCMListeners(): Promise<void> {
  // Listen for foreground notifications
  await FirebaseMessaging.addListener('notificationReceived', (event) => {
    console.log('FCM notification received:', event.notification);
    
    // Display notification using local notification API for consistency
    displayFCMNotification({
      title: event.notification?.title,
      body: event.notification?.body,
      data: (event.notification?.data as Record<string, unknown>) || {},
    });
  });

  // Listen for notification taps
  await FirebaseMessaging.addListener('notificationActionPerformed', async (event) => {
    console.log('FCM notification action performed:', event);
    
    // Trigger haptic feedback when FCM notification is tapped
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style: ImpactStyle.Medium });
      } catch (error) {
        console.error('Error triggering haptic feedback:', error);
      }
    }
    
    if (notificationTapHandler) {
      notificationTapHandler({
        type: 'fcm',
        data: (event.notification?.data as Record<string, unknown>) || {},
      });
    }
  });

  // Listen for token refresh
  FirebaseMessaging.addListener('tokenReceived', (event) => {
    console.log('FCM token refreshed:', event.token);
    // Token will be updated on next app launch
  });
}

/**
 * Setup local notification tap handlers
 */
async function setupLocalNotificationTapHandlers(): Promise<void> {
  await LocalNotifications.addListener('localNotificationActionPerformed', async (notification) => {
    console.log('Local notification action performed:', notification);
    console.log('Action ID:', notification.actionId);
    console.log('Notification data:', notification.notification);
    
    // Trigger haptic feedback
    if (Capacitor.isNativePlatform()) {
      try {
        await Haptics.impact({ style: ImpactStyle.Medium });
      } catch (error) {
        console.error('Error triggering haptic feedback:', error);
      }
    }

    // Handle "Mark Done" action
    if (notification.actionId === 'mark-done') {
      console.log('Mark Done action detected');
      const taskId = notification.notification?.extra?.taskId as string;
      console.log('Task ID from notification:', taskId);
      console.log('Task completion handler exists:', !!taskCompletionHandler);
      
      if (taskId && taskCompletionHandler) {
        try {
          console.log('Calling task completion handler for task:', taskId);
          await taskCompletionHandler(taskId);
          console.log(`✅ Task ${taskId} marked as complete from notification`);
          
          // Cancel the notification after marking done
          await LocalNotifications.cancel({ notifications: [{ id: notification.notification.id }] });
          console.log('Notification cancelled');
        } catch (error) {
          console.error('❌ Error completing task from notification:', error);
        }
      } else {
        console.warn('Cannot mark task done - missing taskId or handler');
      }
      return; // Don't process further to avoid opening app
    }

    // Handle "Remind Later" action
    if (notification.actionId === 'remind-later') {
      console.log('Remind Later action detected');
      const taskId = notification.notification?.extra?.taskId as string;
      console.log('Task ID from notification:', taskId);
      console.log('Remind later handler exists:', !!remindLaterHandler);
      
      if (taskId && remindLaterHandler) {
        try {
          console.log('Calling remind later handler for task:', taskId);
          await remindLaterHandler(taskId);
          console.log(`⏰ Task ${taskId} reminder postponed by 10 minutes`);
          
          // Cancel the current notification
          await LocalNotifications.cancel({ notifications: [{ id: notification.notification.id }] });
          console.log('Notification cancelled, new one will be scheduled');
        } catch (error) {
          console.error('❌ Error postponing reminder:', error);
        }
      } else {
        console.warn('Cannot postpone reminder - missing taskId or handler');
      }
      return; // Don't process further to avoid opening app
    }
    
    // Handle tap on notification body (no actionId means tapped notification itself)
    if (!notification.actionId && notificationTapHandler && notification.notification?.extra) {
      console.log('Notification body tapped, opening app');
      notificationTapHandler({
        type: 'local',
        data: notification.notification.extra,
      });
    }
  });
}

/**
 * Display FCM notification using local notification API
 * This ensures consistent styling with local reminders
 */
async function displayFCMNotification(notification: { title?: string; body?: string; data?: Record<string, unknown> }): Promise<void> {
  try {
    const notificationId = Date.now(); // Use timestamp as ID
    const title = "⚠️ IMPORTANT REMINDER ⚠️\n";
    
    // Trigger vibration for notification
    if (Capacitor.isNativePlatform()) {
      await Haptics.impact({ style: ImpactStyle.Heavy });
    }
    
    await LocalNotifications.schedule({
      notifications: [
        {
          id: notificationId,
          title: title,
          body: notification.body || '',
          largeBody: notification.body || '', // Bigger text on Android
          channelId: 'dsa-push',
          extra: notification.data || {}, // Pass data for tap handler
          smallIcon: 'ic_stat_icon_config_sample',
          iconColor: '#2d5a6b',
          schedule: { at: new Date(Date.now() + 100) }, // Show immediately
        },
      ],
    });
  } catch (error) {
    console.error('Error displaying FCM notification:', error);
  }
}

/**
 * Get FCM token for the current device
 */
export async function getFCMToken(): Promise<string | null> {
  if (!Capacitor.isNativePlatform()) {
    return null;
  }

  try {
    const result = await FirebaseMessaging.getToken();
    return result.token;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
}

/**
 * Register FCM token with the user's profile in Firestore
 */
export async function registerFCMToken(userId: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    const token = await getFCMToken();
    if (!token) {
      console.log('No FCM token available');
      return;
    }

    // Update user document with FCM token
    const userRef = doc(db, 'approvedUsers', userId);
    const { arrayUnion } = await import('firebase/firestore');
    await updateDoc(userRef, {
      fcmTokens: arrayUnion(token),
      lastTokenUpdate: new Date().toISOString(),
    });

    console.log('FCM token registered successfully');
  } catch (error) {
    console.error('Error registering FCM token:', error);
  }
}

/**
 * Delete FCM token (on logout)
 */
export async function deleteFCMToken(userId: string): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    const token = await getFCMToken();
    if (!token) {
      return;
    }

    // Remove token from Firestore
    const userRef = doc(db, 'approvedUsers', userId);
    const { arrayRemove } = await import('firebase/firestore');
    await updateDoc(userRef, {
      fcmTokens: arrayRemove(token),
    });

    // Delete token from FCM
    await FirebaseMessaging.deleteToken();
    
    console.log('FCM token deleted successfully');
  } catch (error) {
    console.error('Error deleting FCM token:', error);
  }
}

/**
 * Remove all notification listeners (on app shutdown)
 */
export async function removeNotificationListeners(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    await FirebaseMessaging.removeAllListeners();
    await LocalNotifications.removeAllListeners();
  } catch (error) {
    console.error('Error removing notification listeners:', error);
  }
}

/**
 * Cancel all scheduled and delivered local notifications
 * Call this on logout to ensure no stale reminders fire for a logged-out user
 */
export async function cancelAllLocalNotifications(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  try {
    // Cancel all pending (scheduled) notifications
    const pending = await LocalNotifications.getPending();
    if (pending.notifications.length > 0) {
      await LocalNotifications.cancel({ notifications: pending.notifications });
      console.log(`Cancelled ${pending.notifications.length} pending local notifications`);
    }
    // Remove already-delivered notifications from the notification tray
    await LocalNotifications.removeAllDeliveredNotifications();
    console.log('All local notifications cleared');
  } catch (error) {
    console.error('Error cancelling all local notifications:', error);
  }
}

// ============================================================================
// Local Reminder Notification Functions (Task-specific)
// ============================================================================

/**
 * Get next notification ID (deterministic hash from taskId)
 */
function getNextNotificationId(taskId: string): number {
  let hash = 0;
  for (let i = 0; i < taskId.length; i++) {
    hash = ((hash << 5) - hash) + taskId.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash);
}

/**
 * Check if notifications are enabled
 */
export async function checkNotificationPermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  try {
    const result = await LocalNotifications.checkPermissions();
    return result.display === 'granted';
  } catch (error) {
    console.error('Failed to check notification permissions:', error);
    return false;
  }
}

/**
 * Schedule a notification for a task reminder
 */
export async function scheduleReminderNotification(task: Task): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    console.log('Not on native platform, skipping notification');
    return false;
  }

  // Tasks use dueDate as the reminder time
  if (!task.dueDate) {
    console.log('Task has no due date set');
    return false;
  }

  const hasPermission = await checkNotificationPermission();
  if (!hasPermission) {
    console.error('Notification permissions not granted');
    console.error('Please enable notifications in device settings');
    return false;
  }

  try {
    const notificationId = getNextNotificationId(task.id);
    const dueDate = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate);

    // Schedule notification 1 minute before the actual due time
    const reminderDate = new Date(dueDate.getTime() - 60000); // Subtract 1 minute (60000ms)

    // Don't schedule if reminder is in the past
    if (reminderDate <= new Date()) {
      console.log('Reminder time is in the past, skipping notification');
      return false;
    }

    const title = `⚠️ IMPORTANT REMINDER ⚠️\n`;
    const body = task.description || 'You have a pending task';

    console.log(`Scheduling notification ${notificationId} for ${reminderDate.toISOString()} (1 min before due time: ${dueDate.toISOString()})`);

    const notification: any = {
      id: notificationId,
      title: title,
      body: body,
      largeBody: body, // Bigger text on Android
      schedule: { 
        at: reminderDate,
        allowWhileIdle: true, // Allow notification even in doze mode
      },
      channelId: 'dsa-reminders',
      extra: { 
        taskId: task.id,
        taskDescription: task.description,
      },
      smallIcon: 'ic_stat_icon_config_sample',
      iconColor: '#FF0000', // Red color
      autoCancel: true, // Auto dismiss when tapped
      vibrate: true, // Must be set on the notification itself; channel setting alone is not always respected
      sound: 'default', // Sound must be present for vibration to trigger on many Android devices
    };

    // Add action buttons if supported
    try {
      notification.actionTypeId = 'TASK_ACTIONS';
    } catch (e) {
      console.log('Action buttons not supported, notification will work without them');
    }

    await LocalNotifications.schedule({
      notifications: [notification],
    });

    console.log(`✅ Successfully scheduled notification ${notificationId} for task ${task.id} at ${reminderDate.toISOString()}`);
    return true;
  } catch (error) {
    console.error('Failed to schedule notification:', error);
    return false;
  }
}

/**
 * Cancel a scheduled notification for a task
 */
export async function cancelReminderNotification(taskId: string): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  try {
    const notificationId = getNextNotificationId(taskId);
    await LocalNotifications.cancel({ notifications: [{ id: notificationId }] });
    console.log(`Cancelled notification ${notificationId} for task ${taskId}`);
    return true;
  } catch (error) {
    console.error('Failed to cancel notification:', error);
    return false;
  }
}

/**
 * Reschedule all pending reminders (on app startup)
 */
export async function rescheduleAllReminders(tasks: Task[]): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  const hasPermission = await checkNotificationPermission();
  if (!hasPermission) {
    console.log('No notification permission, skipping reschedule');
    return;
  }

  // Cancel all existing notifications first
  try {
    await LocalNotifications.removeAllDeliveredNotifications();
    const pending = await LocalNotifications.getPending();
    console.log(`Found ${pending.notifications.length} pending notifications before reschedule`);
  } catch (error) {
    console.error('Failed to remove delivered notifications:', error);
  }

  // Collect every task with a future reminder
  const now = new Date();
  let eligible = tasks.filter(task => {
    if (!task.dueDate || task.completed) {
      return false;
    }
    const reminderDate = task.dueDate instanceof Date ? task.dueDate : new Date(task.dueDate);
    return reminderDate > now;
  });

  // iOS keeps only the 64 soonest-firing pending notifications and silently drops
  // the rest, so schedule the nearest ones ourselves rather than letting the OS
  // choose. Android has no comparable limit and stays unbounded.
  if (Capacitor.getPlatform() === 'ios' && eligible.length > IOS_PENDING_NOTIFICATION_LIMIT) {
    eligible = eligible
      .sort((a, b) => getReminderTime(a) - getReminderTime(b))
      .slice(0, IOS_PENDING_NOTIFICATION_LIMIT);
    console.warn(
      `iOS pending-notification limit: scheduling the ${IOS_PENDING_NOTIFICATION_LIMIT} soonest reminders only`
    );
  }

  let scheduledCount = 0;
  for (const task of eligible) {
    const success = await scheduleReminderNotification(task);
    if (success) {
      scheduledCount++;
    }
  }

  console.log(`✅ Rescheduled ${scheduledCount} reminders from ${tasks.length} tasks`);
}

/**
 * Get all pending notifications
 */
export async function getPendingNotifications(): Promise<Array<{ id: number; title?: string; body?: string }>> {
  if (!Capacitor.isNativePlatform()) {
    return [];
  }

  try {
    const result = await LocalNotifications.getPending();
    return result.notifications;
  } catch (error) {
    console.error('Failed to get pending notifications:', error);
    return [];
  }
}

/**
 * Create a notification document in Firestore
 * A Firebase Function will pick this up and send FCM notifications
 */
export async function sendLeadSharedNotification(
  userIds: string[],
  lead: Lead,
  sharedByName: string,
  note?: string
): Promise<void> {
  try {
    const notificationsRef = collection(db, 'notifications');
    
    // Create notification document for each user
    for (const userId of userIds) {
      await addDoc(notificationsRef, {
        type: 'lead_shared',
        recipientId: userId,
        title: 'Lead Shared With You',
        body: `${sharedByName} shared a lead: ${getLegacyCustomerName(lead)}`,
        data: {
          leadId: lead.id,
          leadName: getLegacyCustomerName(lead),
          sharedBy: sharedByName,
          note: note || '',
        },
        read: false,
        createdAt: Timestamp.now(),
        processed: false, // Firebase Function will set this to true after sending FCM
      });
    }
    
    console.log(`Created ${userIds.length} notification documents for lead sharing`);
  } catch (error) {
    console.error('Error creating notification documents:', error);
    throw error;
  }
}

/**
 * Create a notification document for partner sharing
 * A Firebase Function will pick this up and send FCM notifications
 */
export async function sendPartnerSharedNotification(
  userIds: string[],
  partner: { id: string; name: string; type: string },
  sharedByName: string,
  note?: string
): Promise<void> {
  try {
    const notificationsRef = collection(db, 'notifications');
    
    // Create notification document for each user
    for (const userId of userIds) {
      await addDoc(notificationsRef, {
        type: 'partner_shared',
        recipientId: userId,
        title: 'Channel Partner Shared With You',
        body: `${sharedByName} shared a channel partner: ${partner.name}`,
        data: {
          partnerId: partner.id,
          partnerName: partner.name,
          partnerType: partner.type,
          sharedBy: sharedByName,
          note: note || '',
        },
        read: false,
        createdAt: Timestamp.now(),
        processed: false, // Firebase Function will set this to true after sending FCM
      });
    }
    
    console.log(`Created ${userIds.length} notification documents for partner sharing`);
  } catch (error) {
    console.error('Error creating notification documents:', error);
    throw error;
  }
}
